<template>
  <div class="anamnesis-subset">
    <!-- Toolbar réutilisable -->
    <SubsetToolbar
      :sort-options="sortOptions"
      :sort-by="sortBy"
      :sort-direction="sortDirection"
      :is-card-view="isCardView"
      :search-query="searchQuery"
      search-placeholder="Rechercher un agent..."
      create-button-text="Nouvel agent"
      :show-create-button="true"
      @update:sort-by="handleUpdateSortBy"
      @update:search-query="handleUpdateSearchQuery"
      @toggle-sort-direction="handleToggleSortDirection"
      @toggle-view="handleToggleView"
      @create="$emit('create')"
    >
      <!-- Slot filtres : Filtres spécifiques aux agents -->
      <template #filters>
        <!-- Filtre par type d'agent -->
        <button
          class="px-3 py-1.5 rounded-full text-sm transition-colors duration-200 flex items-center space-x-1.5"
          :class="[
            agentTypeFilter === 'all'
              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
          ]"
          @click="handleToggleAgentTypeFilter('all')"
        >
          <span>Tous</span>
        </button>
        
        <button
          class="px-3 py-1.5 rounded-full text-sm transition-colors duration-200 flex items-center space-x-1.5"
          :class="[
            agentTypeFilter === 'physical'
              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
          ]"
          @click="handleToggleAgentTypeFilter('physical')"
        >
          <UserIcon class="h-4 w-4" />
          <span>Physiques</span>
        </button>
        
        <button
          class="px-3 py-1.5 rounded-full text-sm transition-colors duration-200 flex items-center space-x-1.5"
          :class="[
            agentTypeFilter === 'legal'
              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
          ]"
          @click="handleToggleAgentTypeFilter('legal')"
        >
          <BuildingIcon class="h-4 w-4" />
          <span>Morales</span>
        </button>
        
        <!-- Filtre IdRef -->
        <button
          class="px-3 py-1.5 rounded-full text-sm transition-colors duration-200 flex items-center space-x-1.5"
          :class="[
            showIdRef
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
          ]"
          @click="handleToggleIdRefFilter"
        >
          <LinkIcon class="h-4 w-4" />
          <span>IdRef</span>
        </button>
      </template>
    </SubsetToolbar>

    <!-- Liste des agents -->
    <div v-if="paginatedAgents.length > 0" :class="{ 'anamnesis-grid': isCardView, 'anamnesis-table': !isCardView }">
      <!-- En-tête du tableau (mode table uniquement) -->
      <TableHeader 
        v-if="!isCardView" 
        :columns="tableColumns" 
      />
      
      <AgentCard 
        v-for="agent in paginatedAgents"
        :key="agent.uuid"
        :agent="agent"
        :roles="agent.roles"
        :view-mode="isCardView ? 'default' : 'table'"
        :show-actions="showActions"
        :show-edit-button="showEditButton"
        :show-delete-button="showDeleteButton"
        :is-owned="isAgentOwned(agent)"
        :is-public="agent.is_public"
        :show-ownership-badge="true"
        :user-id="agent.user_id"
        @edit="emit('edit', agent)"
        @delete="emit('delete', agent)"
      />
    </div>

    <div v-else class="text-center py-8">
      <p class="text-gray-500">Aucun agent trouvé</p>
    </div>

    <!-- Compteur d'agents -->
    <div class="flex justify-end mt-4 mb-2">
      <p class="text-sm text-gray-400">
        {{ filteredAgents.length }} agent{{ filteredAgents.length > 1 ? 's' : '' }} trouvé{{ filteredAgents.length > 1 ? 's' : '' }}
      </p>
    </div>

    <!-- Pagination -->
    <div v-if="filteredAgents.length > itemsPerPage" class="mt-4 flex justify-center space-x-2">
      <button
        :disabled="currentPage === 1"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === 1 ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="handlePreviousPage"
      >
        Précédent
      </button>
      <button
        v-for="page in totalPages"
        :key="page"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === page ? 'bg-blue-500 text-white border-blue-500' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="currentPage = page"
      >
        {{ page }}
      </button>
      <button
        :disabled="currentPage === totalPages"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === totalPages ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="handleNextPage"
      >
        Suivant
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Building as BuildingIcon, Link as LinkIcon, User as UserIcon } from 'lucide-vue-next';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import type { AgentWithMemos } from '../../api/types/entities/agent/relations';
import { useAuthStore } from '../../stores/auth';
import type { SortOption } from '../UI/SubsetToolbar.vue';
import SubsetToolbar from '../UI/SubsetToolbar.vue';
import type { TableColumn } from '../UI/TableHeader.vue';
import TableHeader from '../UI/TableHeader.vue';
import AgentCard from '../cards/AgentCard.vue';

// Types stricts
type SortBy = 'name' | 'affiliation' | 'group' | 'date' | 'roles';
type SortDirection = 'asc' | 'desc';

// Props avec types stricts
interface Props {
  agents: AgentWithMemos[];
  showActions?: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  itemsPerPage?: number;
  defaultSort?: SortBy;
}

const props = withDefaults(defineProps<Props>(), {
  showActions: true,
  showEditButton: true,
  showDeleteButton: true,
  itemsPerPage: 20,
  defaultSort: 'name',
});

// Events avec types stricts
const emit = defineEmits<{
  create: [];
  edit: [agent: AgentWithMemos];
  delete: [agent: AgentWithMemos];
}>();

// Store auth
const authStore = useAuthStore();

// État réactif avec types stricts
const isCardView: Ref<boolean> = ref(true);
const searchQuery: Ref<string> = ref('');
const sortBy: Ref<SortBy> = ref(props.defaultSort);
const sortDirection: Ref<SortDirection> = ref('asc');
const showIdRef: Ref<boolean> = ref(false);
const agentTypeFilter: Ref<'all' | 'physical' | 'legal'> = ref('all');
const currentPage: Ref<number> = ref(1);

// Options de tri avec types stricts
const sortOptions: ComputedRef<SortOption[]> = computed(() => [
  { value: 'name', label: 'Nom' },
  { value: 'affiliation', label: 'Affiliation' },
  { value: 'group', label: 'Groupe' },
  { value: 'date', label: 'Date' },
  { value: 'roles', label: 'Rôles' },
]);

// Colonnes du tableau avec types stricts
const tableColumns: ComputedRef<TableColumn[]> = computed(() => [
  { label: 'Agent', class: 'flex-[1.25]' },
  { label: 'Affiliation', class: 'hidden md:flex flex-1' },
  { label: 'Rôles', class: 'hidden lg:flex flex-1' },
  { label: 'Actions', class: 'ml-auto' },
]);

// Handlers avec types de retour explicites
const handleUpdateSortBy = (value: string): void => {
  sortBy.value = value as SortBy;
};

const handleUpdateSearchQuery = (value: string): void => {
  searchQuery.value = value;
};

const handleToggleSortDirection = (): void => {
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
};

const handleToggleView = (): void => {
  isCardView.value = !isCardView.value;
};

const handleToggleIdRefFilter = (): void => {
  showIdRef.value = !showIdRef.value;
};

const handleToggleAgentTypeFilter = (type: 'all' | 'physical' | 'legal'): void => {
  agentTypeFilter.value = type;
};

const handlePreviousPage = (): void => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const handleNextPage = (): void => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

// Fonction pour vérifier la propriété d'un agent
const isAgentOwned = (agent: AgentWithMemos): boolean => {
  return authStore.isAssetOwner(agent);
};

// Fonction de recherche typée correctement
const searchInAgent = (agent: AgentWithMemos, query: string): boolean => {
  const searchTerm = query.toLowerCase();
  
  // Recherche dans les champs principaux
  if (agent.first_name?.toLowerCase().includes(searchTerm)) return true;
  if (agent.last_name?.toLowerCase().includes(searchTerm)) return true;
  if (agent.affiliation?.toLowerCase().includes(searchTerm)) return true;
  if (agent.group?.toLowerCase().includes(searchTerm)) return true;
  
  // Recherche dans les rôles (string, pas array)
  if (agent.roles?.toLowerCase().includes(searchTerm)) return true;
  
  // Recherche dans l'IdRef
  if (agent.idref_id?.toLowerCase().includes(searchTerm)) return true;
  
  // Recherche dans l'UUID
  if (agent.uuid?.toLowerCase().includes(searchTerm)) return true;
  
  return false;
};

// Filtrage et tri avec types stricts
const filteredAgents: ComputedRef<AgentWithMemos[]> = computed(() => {
  let result: AgentWithMemos[] = [...props.agents];

  // Filtrage par type d'agent
  if (agentTypeFilter.value !== 'all') {
    result = result.filter((agent: AgentWithMemos): boolean => {
      if (agentTypeFilter.value === 'physical') {
        return !agent.is_legal_entity;
      } else if (agentTypeFilter.value === 'legal') {
        return Boolean(agent.is_legal_entity);
      }
      return true;
    });
  }

  // Filtrage par IdRef
  if (showIdRef.value) {
    result = result.filter((agent: AgentWithMemos): boolean => 
      Boolean(agent.idref_id)
    );
  }

  // Filtrage par recherche
  if (searchQuery.value) {
    result = result.filter((agent: AgentWithMemos): boolean => 
      searchInAgent(agent, searchQuery.value)
    );
  }

  // Tri
  result.sort((a: AgentWithMemos, b: AgentWithMemos): number => {
    let comparison: number = 0;
    
    switch (sortBy.value) {
      case 'name': {
        const aName = [a.first_name, a.last_name].filter(Boolean).join(' ') || '';
        const bName = [b.first_name, b.last_name].filter(Boolean).join(' ') || '';
        comparison = aName.localeCompare(bName);
        break;
      }
      case 'affiliation':
        comparison = (a.affiliation || '').localeCompare(b.affiliation || '');
        break;
      case 'group':
        comparison = (a.group || '').localeCompare(b.group || '');
        break;
      case 'date':
        comparison = new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        break;
      case 'roles':
        comparison = (a.roles || '').localeCompare(b.roles || '');
        break;
    }
    
    return sortDirection.value === 'asc' ? comparison : -comparison;
  });

  return result;
});

// Pagination avec types stricts
const totalPages: ComputedRef<number> = computed(() => 
  Math.ceil(filteredAgents.value.length / props.itemsPerPage)
);

const paginatedAgents: ComputedRef<AgentWithMemos[]> = computed(() => {
  const start: number = (currentPage.value - 1) * props.itemsPerPage;
  const end: number = start + props.itemsPerPage;
  return filteredAgents.value.slice(start, end);
});

// Watchers avec types stricts
watch([searchQuery, showIdRef, agentTypeFilter, sortBy], (): void => {
  currentPage.value = 1;
});
</script>

<style scoped>
.anamnesis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.anamnesis-table {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style> 